import React from 'react';
import withShipment from '../../../withShipment';
import SettingsToolbar from '../SettingsToolbar';
import {
  grantMaintenancePermit,
  revokeMaintenancePermit,
  loadMaintenancePermit,
} from '../../../redux/actions/settings';
import {
  maintenancePermitSelector,
  maintenancePermitHistorySelector,
} from '../../../redux/selectors/settings';
import { userInfoSelector } from '../../../redux/selectors/auth';
import {
  SCOPES,
  S_INITIAL,
  S_CONFIRM,
  S_ACTIVE,
  S_REVOKE_CONFIRM,
  S_REVOKED,
  S_EXPIRED,
  STATUS_ACTIVE,
  STATUS_REVOKED,
  STATUS_EXPIRED,
  HISTORY_GRANTED,
  HISTORY_REVOKED,
  GREEN,
  RED,
} from './constants';
import './MaintenancePermit.css';

const pad2 = (n) => (n < 10 ? `0${n}` : `${n}`);

const fmtDate = (ts) =>
  new Date(ts).toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });

const fmtTime = (ts) =>
  new Date(ts).toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
  });

class MaintenancePermit extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      screen: S_INITIAL,
      countdown: '',
      pct: 100,
      localPermit: null,
    };
    this.tickInterval = null;
  }

  componentDidMount() {
    const locationId = this.getLocationId();
    if (locationId) {
      this.props.loadMaintenancePermit({ locationId });
    }
  }

  componentDidUpdate(prevProps) {
    if (prevProps.maintenancePermit !== this.props.maintenancePermit) {
      const permit = this.props.maintenancePermit;
      if (permit && permit.status === STATUS_ACTIVE) {
        this.setState(
          { screen: S_ACTIVE, localPermit: permit },
          this.startTicker
        );
      } else if (!permit) {
        this.stopTicker();
        if (
          this.state.screen !== S_REVOKED &&
          this.state.screen !== S_EXPIRED &&
          this.state.screen !== S_CONFIRM &&
          this.state.screen !== S_REVOKE_CONFIRM
        ) {
          this.setState({
            screen: S_INITIAL,
            localPermit: null,
            countdown: '',
            pct: 100,
          });
        }
      }
    }
  }

  componentWillUnmount() {
    this.stopTicker();
  }

  getLocationId = () =>
    this.props.userInfo && this.props.userInfo.selectedLocationId;

  getUserId = () => this.props.userInfo && this.props.userInfo.userId;

  getDisplayName = () =>
    this.props.userInfo &&
    (this.props.userInfo.displayName || this.props.userInfo.username);

  startTicker = () => {
    this.stopTicker();
    if (this.tick()) return;
    this.tickInterval = setInterval(() => {
      if (this.tick()) this.stopTicker();
    }, 1000);
  };

  stopTicker = () => {
    if (this.tickInterval) {
      clearInterval(this.tickInterval);
      this.tickInterval = null;
    }
  };

  tick = () => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    if (!permit || !permit.endDate) return true;
    const diff = permit.endDate - Date.now();
    if (diff <= 0) {
      this.setState({ countdown: '00:00:00', pct: 0, screen: S_EXPIRED });
      const locationId = this.getLocationId();
      if (locationId) {
        this.props.loadMaintenancePermit({ locationId });
      }
      return true;
    }
    const h = Math.floor(diff / 3600000);
    const m = Math.floor((diff % 3600000) / 60000);
    const s = Math.floor((diff % 60000) / 1000);
    this.setState({
      countdown: `${pad2(h)}:${pad2(m)}:${pad2(s)}`,
      pct: Math.round((diff / 86400000) * 100),
    });
    return false;
  };

  handleGrantClick = () => this.setState({ screen: S_CONFIRM });

  handleGrantConfirm = () => {
    this.props.grantMaintenancePermit(
      {
        locationId: this.getLocationId(),
        userId: this.getUserId(),
        displayName: this.getDisplayName(),
      },
      (err, permit) => {
        if (!err && permit) {
          this.setState(
            { screen: S_ACTIVE, localPermit: permit },
            this.startTicker
          );
        }
      }
    );
  };

  handleRevokeClick = () => this.setState({ screen: S_REVOKE_CONFIRM });

  handleRevokeConfirm = () => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    if (!permit) return;
    this.props.revokeMaintenancePermit(
      {
        locationId: this.getLocationId(),
        permitId: permit.permitId,
        userId: this.getUserId(),
        displayName: this.getDisplayName(),
      },
      (err) => {
        if (!err) {
          this.stopTicker();
          this.setState({ screen: S_REVOKED });
        }
      }
    );
  };

  handleReset = () => {
    this.stopTicker();
    this.setState({
      screen: S_INITIAL,
      localPermit: null,
      countdown: '',
      pct: 100,
    });
  };

  renderLabel = (text) => <div className="mp-label">{text}</div>;

  renderVal = (text, color) => (
    <div className="mp-val" style={color ? { color } : undefined}>
      {text}
    </div>
  );

  renderField = (label, value, sub, color) => (
    <div>
      {this.renderLabel(label)}
      {this.renderVal(value, color)}
      {sub && <div className="mp-sub">{sub}</div>}
    </div>
  );

  renderChip = (label, opts = {}) => {
    const style = {};
    if (opts.bg) style.background = opts.bg;
    if (opts.bdr) style.borderColor = opts.bdr;
    if (opts.color) style.color = opts.color;
    return (
      <span key={label} className="mp-chip" style={style}>
        {opts.icon && (
          <span
            className="mp-chip-icon"
            style={opts.iconColor ? { color: opts.iconColor } : undefined}
          >
            {opts.icon}
          </span>
        )}
        {label}
      </span>
    );
  };

  renderAlert = (type, title, body, compact) => (
    <div
      className={`mp-alert mp-alert-${type}${
        compact ? ' mp-alert-compact' : ''
      }`}
    >
      {title && <div className="mp-alert-title">{title}</div>}
      <div className="mp-alert-body">{body}</div>
    </div>
  );

  renderHistoryLog = () => {
    const history = this.props.maintenancePermitHistory || [];
    if (history.length === 0) return null;
    // Server already sorts history desc by timestamp, so slicing from
    // the head gives us the ten most-recent entries. Capping the list
    // keeps the panel from growing unboundedly for busy locations.
    const HISTORY_LIMIT = 10;
    const visible = history.slice(0, HISTORY_LIMIT);
    const dotColor = (action) => {
      if (action === HISTORY_GRANTED) return GREEN;
      if (action === HISTORY_REVOKED) return RED;
      return '#f59e0b';
    };
    return (
      <div className="mp-history">
        <div className="mp-section-title mp-first">Permit History</div>
        <div className="mp-history-list">
          {visible.map((e, i) => (
            <div
              key={`${e.permitId}-${e.timestamp}-${i}`}
              className={`mp-history-row ${i % 2 === 0 ? 'mp-even' : 'mp-odd'}`}
            >
              <div
                className="mp-history-dot"
                style={{ background: dotColor(e.action) }}
              />
              <div style={{ flex: 1 }}>
                <span className="mp-history-action">
                  {e.action.charAt(0) + e.action.slice(1).toLowerCase()}
                </span>
                <span className="mp-history-pid">{e.permitId}</span>
              </div>
              <span className="mp-history-time">{fmtTime(e.timestamp)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  renderPermitCard = (status) => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    if (!permit) return null;
    const dead = status === STATUS_REVOKED || status === STATUS_EXPIRED;
    const statusColor =
      status === STATUS_ACTIVE
        ? GREEN
        : status === STATUS_REVOKED
        ? RED
        : '#f59e0b';
    const statusLabel =
      status === STATUS_ACTIVE
        ? 'Active'
        : status === STATUS_REVOKED
        ? 'Revoked'
        : 'Expired';
    return (
      <div className="mp-permit-card">
        <div className="mp-permit-header">
          <span className="mp-permit-header-title">MAINTENANCE PERMIT</span>
          <span className="mp-permit-id-badge">{permit.permitId}</span>
        </div>
        <div className="mp-permit-body">
          <div className="mp-field-grid">
            {this.renderField(
              'Authorized By',
              permit.grantedByDisplayName || 'Owner'
            )}
            {this.renderField(
              'Company',
              (this.props.userInfo &&
                this.props.userInfo.selectedLocationName) ||
                '—'
            )}
            {this.renderField(
              'Granted On',
              permit.startDate ? fmtDate(permit.startDate) : '—',
              permit.startDate ? fmtTime(permit.startDate) : null
            )}
            {this.renderField(
              'Expires On',
              permit.endDate ? fmtDate(permit.endDate) : '—',
              permit.endDate ? fmtTime(permit.endDate) : null
            )}
            {this.renderField(
              'Duration',
              `${permit.durationHours || 24} Hours`
            )}
            <div>
              {this.renderLabel('Status')}
              <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                <div
                  className="mp-status-dot"
                  style={{ background: statusColor }}
                />
                <div className="mp-val" style={{ color: statusColor }}>
                  {statusLabel}
                </div>
              </div>
            </div>
          </div>
          <div className="mp-divider" />
          <div className="mp-section-title mp-first">Access Scope</div>
          <div className="mp-scopes">
            {(permit.scopes || SCOPES).map((s) =>
              this.renderChip(s, {
                icon: dead ? '\u2717' : '\u2713',
                iconColor: dead ? '#999' : GREEN,
                bg: dead ? '#f5f5f5' : undefined,
                bdr: dead ? '#e0e0e0' : undefined,
                color: dead ? '#999' : undefined,
              })
            )}
          </div>
          <div className="mp-section-title">Granted To</div>
          <div className="mp-granted-to">
            <div className="mp-nt-avatar">NT</div>
            <div>
              <div className="mp-nt-name">NuVe Tech Admins</div>
              <div className="mp-nt-sub">System maintenance team</div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  renderInitial = () => (
    <div className="mp-card">
      {/*<div className="mp-title">Maintenance Permit</div>*/}
      <div className="mp-title">Grant temporary access to NuVe Tech Admins</div>
      <div className="mp-divider" />
      <p
        style={{
          fontSize: 14,
          color: '#555',
          lineHeight: 1.6,
          margin: '0 0 14px',
        }}
      >
        Allow temporary system access for maintenance or troubleshooting. This
        will grant{' '}
        <strong style={{ color: '#0a1244' }}>NuVe Tech Admins</strong> access
        to:
      </p>
      <div className="mp-scopes">
        {SCOPES.map((s) =>
          this.renderChip(s, {
            bg: 'rgba(10,18,68,0.04)',
            bdr: 'rgba(10,18,68,0.1)',
            color: '#333',
          })
        )}
      </div>
      {this.renderAlert(
        'info',
        null,
        'Access is time-limited to 24 hours and all activity is logged.',
        true
      )}
      <button
        className="mp-btn mp-btn-primary mp-btn-full"
        onClick={this.handleGrantClick}
      >
        Grant Access (24H)
      </button>
      {this.renderHistoryLog()}
    </div>
  );

  renderConfirm = () => (
    <div className="mp-card">
      <div className="mp-title" style={{ marginBottom: 14 }}>
        Maintenance Permit
      </div>
      {this.renderAlert(
        'warning',
        'Confirm Access',
        <>
          This will grant <strong>NuVe Tech Admins</strong> full access to your
          system data, profiles, settings, and reports for{' '}
          <strong>24 hours</strong>. All activity will be recorded.
        </>
      )}
      <div style={{ marginBottom: 16 }}>
        <div
          style={{
            fontSize: 12,
            color: '#92400e',
            fontWeight: 600,
            marginBottom: 6,
          }}
        >
          Access includes:
        </div>
        <div className="mp-scopes" style={{ marginBottom: 0 }}>
          {SCOPES.map((s) =>
            this.renderChip(s, {
              bg: 'rgba(146,64,14,0.06)',
              bdr: 'rgba(146,64,14,0.15)',
              color: '#92400e',
            })
          )}
        </div>
      </div>
      <div className="mp-btn-row">
        <button
          className="mp-btn mp-btn-danger mp-btn-full"
          onClick={this.handleGrantConfirm}
        >
          Yes, Grant Access
        </button>
        <button
          className="mp-btn mp-btn-ghost mp-btn-full"
          onClick={() => this.setState({ screen: S_INITIAL })}
        >
          Cancel
        </button>
      </div>
    </div>
  );

  renderActive = () => {
    const { countdown, pct } = this.state;
    const fillColor = pct > 25 ? GREEN : pct > 10 ? '#f59e0b' : RED;
    return (
      <div>
        <div className="mp-countdown-banner">
          <div className="mp-countdown-header">
            <div>
              <div className="mp-countdown-title">Access Granted</div>
              <div className="mp-countdown-sub">Permit is currently active</div>
            </div>
            <div className="mp-live-dot">
              <div className="mp-live-dot-circle" />
              <span className="mp-live-label">LIVE</span>
            </div>
          </div>
          <div className="mp-countdown-time">{countdown || '24:00:00'}</div>
          <div className="mp-progress-bar">
            <div
              className="mp-progress-fill"
              style={{ width: `${pct}%`, background: fillColor }}
            />
          </div>
          <div className="mp-progress-labels">
            <span>remaining</span>
            <span>24h total</span>
          </div>
        </div>
        {this.renderPermitCard(STATUS_ACTIVE)}
        <button
          className="mp-btn mp-btn-outline-danger mp-btn-full"
          onClick={this.handleRevokeClick}
          style={{ marginTop: 12 }}
        >
          Revoke Access Now
        </button>
        {this.renderHistoryLog()}
      </div>
    );
  };

  renderRevokeConfirm = () => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    return (
      <div className="mp-card">
        <div className="mp-title" style={{ marginBottom: 14 }}>
          Revoke Access
        </div>
        {this.renderAlert(
          'error',
          'This action is immediate',
          <>
            Revoking will immediately terminate all NuVe Tech Admin access.
            Permit <strong>{permit ? permit.permitId : ''}</strong> will be
            deactivated.
          </>
        )}
        <div className="mp-btn-row">
          <button
            className="mp-btn mp-btn-danger mp-btn-full"
            onClick={this.handleRevokeConfirm}
          >
            Yes, Revoke Now
          </button>
          <button
            className="mp-btn mp-btn-ghost mp-btn-full"
            onClick={() => this.setState({ screen: S_ACTIVE })}
          >
            Keep Active
          </button>
        </div>
      </div>
    );
  };

  renderRevoked = () => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    return (
      <div>
        {this.renderAlert(
          'error',
          'Access Revoked',
          <>
            Permit <strong>{permit ? permit.permitId : ''}</strong> has been
            revoked. All NuVe Tech Admin access has been terminated.
          </>
        )}
        {this.renderPermitCard(STATUS_REVOKED)}
        <div style={{ marginTop: 14 }}>
          <button
            className="mp-btn mp-btn-primary mp-btn-full"
            onClick={this.handleReset}
          >
            Back to Maintenance Permit
          </button>
        </div>
        {this.renderHistoryLog()}
      </div>
    );
  };

  renderExpired = () => {
    const permit = this.state.localPermit || this.props.maintenancePermit;
    return (
      <div>
        {this.renderAlert(
          'warning',
          'Permit Expired',
          <>
            Permit <strong>{permit ? permit.permitId : ''}</strong> has expired.
            Access was automatically terminated after 24 hours.
          </>
        )}
        {this.renderPermitCard(STATUS_EXPIRED)}
        <div style={{ marginTop: 14 }}>
          <button
            className="mp-btn mp-btn-primary mp-btn-full"
            onClick={this.handleReset}
          >
            Back to Maintenance Permit
          </button>
        </div>
        {this.renderHistoryLog()}
      </div>
    );
  };

  render() {
    const { screen } = this.state;
    return (
      <React.Fragment>
        <SettingsToolbar title="Maintenance Permit" hideCreate />
        <div className="mp-container">
          {screen === S_INITIAL && this.renderInitial()}
          {screen === S_CONFIRM && this.renderConfirm()}
          {screen === S_ACTIVE && this.renderActive()}
          {screen === S_REVOKE_CONFIRM && this.renderRevokeConfirm()}
          {screen === S_REVOKED && this.renderRevoked()}
          {screen === S_EXPIRED && this.renderExpired()}
        </div>
      </React.Fragment>
    );
  }
}

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  maintenancePermit: maintenancePermitSelector(state),
  maintenancePermitHistory: maintenancePermitHistorySelector(state),
});

const actionCreators = {
  grantMaintenancePermit,
  revokeMaintenancePermit,
  loadMaintenancePermit,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  MaintenancePermit
);
